import { FC, useMemo } from 'react';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import {
  DataObjectCloneEntityType,
  DataObjectContextProvider,
  DataObjectForm,
  DataObjectFormDataType,
  DataObjectType,
  DataObjectView,
  PermissionCode,
  Spinner,
  useGetDataObjectVersionEditQuery,
  useHasPermission,
  useHasPermissionForVersion,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { RouteObject } from '../../common/types';
import {
  DataObjectBreadcrumbs,
  DataObjectBreadcrumbsData,
} from './DataObjectBreadcrumbs';
import { useOpenTestCalculationReport } from './useOpenTestCalculation';
import { useOpenTestValidationReport } from './useOpenTestValidation';

export const DataObjectClone: FC<{
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onSuccess: (params?: { id: number; mode?: string }) => void;
  onCancel: (params?: { id: number; mode?: string }) => void;
}> = (props) => {
  const versionId = useNumericParam('id');
  const [{ entityType }] = useQueryParams();
  const hasPermissionForVersion = useHasPermissionForVersion();
  const hasPermission = useHasPermission();

  if (!versionId || !entityType) {
    return <NotFound />;
  }

  const permissionCode =
    props.view.dataObjectType === DataObjectType.IN
      ? PermissionCode.INPUT_DESC_MANAGE
      : PermissionCode.OUTPUT_DESC_MANAGE;
  const isAllowed =
    entityType === 'description'
      ? hasPermission(permissionCode)
      : hasPermissionForVersion(permissionCode, versionId);

  return (
    <Forbidden isAllowed={isAllowed}>
      <DataObjectContextProvider>
        <DataObjectCloneInner
          {...props}
          versionId={versionId}
          entityType={entityType as DataObjectCloneEntityType}
        />
      </DataObjectContextProvider>
    </Forbidden>
  );
};

const DataObjectCloneInner: FC<{
  versionId: number;
  entityType: DataObjectCloneEntityType;
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onSuccess: (params?: { id: number; mode?: string }) => void;
  onCancel: (params?: { id: number; mode?: string }) => void;
}> = (props) => {
  const { versionId, entityType, routes, view, onSuccess, onCancel } = props;
  const { data, isLoading } = useGetDataObjectVersionEditQuery({
    id: versionId,
    clone: true,
  });
  const openTestCalculationReport = useOpenTestCalculationReport();
  const openTestValidationReport = useOpenTestValidationReport();

  const dataObjectVersion = useMemo<DataObjectFormDataType | undefined>(
    () =>
      data && {
        ...data,
        statusCode: 'DRAFT',
        code: entityType === 'version' ? data.code : undefined,
        dataObjectId: entityType === 'version' ? data.dataObjectId : null,
        allVersions: entityType === 'version' ? data.allVersions : [],
      },
    [data, entityType],
  );

  if (!dataObjectVersion && isLoading) {
    return <Spinner />;
  }

  if (!dataObjectVersion) {
    return null;
  }

  return (
    <DataObjectForm
      dataObjectView={view}
      mode="clone"
      dataObjectDescriptionVersion={dataObjectVersion}
      onSuccess={onSuccess}
      onClickCancel={() => onCancel({ id: versionId })}
      breadCrumb={
        <DataObjectBreadcrumbs data={{ dataObjectVersion }} routes={routes} />
      }
      newVersion={entityType === 'version'}
      openTestCalculate={openTestCalculationReport}
      openTestValidate={openTestValidationReport}
    />
  );
};
